/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Tenant storefront page. Reproduces the aegis-ao-rental public Home design:
 * a black hero with a white booking-form card + promo side + feature highlights,
 * a light "Available cars" section with vehicle cards grouped by category, and a CTA.
 * Adapted to the myezwheels storefront API (flat Car[] from /storefront/search).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Car as CarIcon, ArrowRight, Calendar, Users, MapPin, Star, Zap, ShieldCheck } from 'lucide-react';
import { pickLocalized, pickFromMap, categoryLabel } from '../utils/localized';
import type { Car, CarSearchRequest } from '../types';
import { RentalType } from '../types';
import api from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import ModelImage from '../components/ModelImage';
import ModelCategoryList from '../components/ModelCategoryList';
import CitySearchInput, { CitySearchHandle } from '../components/CitySearchInput';
import ViewModeToggle, { useViewMode } from '../components/ViewModeToggle';
import SectionsRenderer, { parseSections } from '../components/SectionsRenderer';
import { priceUnit, fmtPrice, tierList } from '../utils/pricing';

function todayISO(offsetDays = 0): string {
  return new Date(Date.now() + offsetDays * 864e5).toISOString().slice(0, 10);
}

function buildRequest(startDate: string, endDate: string, city: string): CarSearchRequest {
  return {
    city: city || undefined,
    // No geo filter — show the tenant's whole fleet (many cars have no location).
    latitude: 0,
    longitude: 0,
    dateFrom: new Date(startDate || todayISO()).toISOString(),
    dateTo: new Date(endDate || todayISO(7)).toISOString(),
    rentalType: 0, // storefront filters by category, not rental-type bitmask
    distance: 0,
    typesIds: [],
  };
}

export default function StorefrontPage() {
  const { t, i18n } = useTranslation();
  const { tenant } = useTenant();
  const modelsRef = useRef<HTMLDivElement>(null);
  const cityRef = useRef<CitySearchHandle>(null);

  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO(7));
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [cities, setCities] = useState<string[]>([]);
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  // The company decides how its fleet is listed (Storefront Design → Main):
  //   'models' / 'cars' — fixed, no switch;  'both' — the visitor picks (default).
  const displayMode = tenant?.vehicleDisplayMode ?? 'both';
  const [viewMode, setViewMode] = useViewMode('cars');
  const effectiveMode = displayMode === 'both' ? viewMode : displayMode;

  const runSearch = async (cityOverride?: string) => {
    setLoading(true);
    try {
      setCars(await api.search(buildRequest(startDate, endDate, cityOverride ?? city)));
    } catch {
      setCars([]);
    } finally {
      setLoading(false);
    }
  };

  // Re-run the search immediately when the city changes (no extra button click).
  const changeCity = (c: string) => {
    setCity(c);
    runSearch(c);
  };

  useEffect(() => {
    runSearch();
    api
      .getCities()
      .then(setCities)
      .catch(() => setCities([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Category chips/labels are localized → clear the filter on language change (stale label won't match).
  useEffect(() => { setCategory(''); }, [i18n.language]);

  // Distinct categories from the fleet (CarCategories segment), localized + in catalog order.
  const categories = useMemo(() => {
    const order = new Map<string, number>();
    for (const c of cars) {
      const name = categoryLabel(c, i18n.language);
      if (!name) continue;
      order.set(name, Math.min(order.get(name) ?? 99, c.categoryPosition ?? 99));
    }
    return Array.from(order.entries())
      .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
      .map(([name]) => name);
  }, [cars, i18n.language]);

  // Fleet narrowed to the selected category (used by both view modes).
  const filtered = useMemo(
    () =>
      cars.filter(
        (c) =>
          !category ||
          categoryLabel(c, i18n.language).toLowerCase() === category.toLowerCase(),
      ),
    [cars, category, i18n.language],
  );

  // Group cars by category for the per-car view.
  const grouped = useMemo(() => {
    const map = new Map<string, Car[]>();
    const order = new Map<string, number>();
    filtered.forEach((c) => {
        const key = categoryLabel(c, i18n.language, 'Other');
        order.set(key, Math.min(order.get(key) ?? 99, c.categoryPosition ?? 99));
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(c);
      });
    return Array.from(map.entries()).sort(
      (a, b) => (order.get(a[0]) ?? 99) - (order.get(b[0]) ?? 99) || a[0].localeCompare(b[0]),
    );
  }, [filtered, i18n.language]);

  const scrollToModels = () =>
    setTimeout(() => modelsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);

  // Content sections (Design editor → Content tab). Like aegis-ao-rental_web, the FIRST section feeds
  // the hero (title/description + the feature highlights from its notes); the rest render after the
  // models grid. Falls back to the tag line / About text / bundled defaults when there is no section.
  const contentSections = useMemo(() => parseSections(tenant?.contentJson), [tenant?.contentJson]);
  const heroSection = contentSections[0];

  const heroTitle =
    pickFromMap(heroSection?.title, i18n.language) ||
    pickLocalized(tenant?.tagLineJson, tenant?.tagLine, i18n.language) ||
    t('hero.title');
  const heroDescription =
    pickFromMap(heroSection?.description, i18n.language) ||
    t('hero.subtitle');

  const defaultFeatures = [
    { icon: <Zap className="h-6 w-6" />, title: t('hero.instantTitle'), text: t('hero.instantText') },
    { icon: <ShieldCheck className="h-6 w-6" />, title: t('hero.insuredTitle'), text: t('hero.insuredText') },
    { icon: <CarIcon className="h-6 w-6" />, title: t('hero.fleetTitle'), text: t('hero.fleetText') },
  ];

  // Hero feature highlights from the first section's notes (icon = its SVG symbol, else a default car
  // glyph). When the first section has no notes, keep the three bundled defaults.
  const heroNotes = (heroSection?.notes || [])
    .map((n) => ({
      title: pickFromMap(n.title, i18n.language),
      text: pickFromMap(n.text, i18n.language) || pickFromMap(n.caption, i18n.language),
      symbol: n.symbol?.trim().startsWith('<svg') ? n.symbol : null,
      symbolForeColor: n.symbolForeColor,
    }))
    .filter((n) => n.title || n.text || n.symbol);
  const features =
    heroNotes.length > 0
      ? heroNotes.map((n) => ({
          icon: n.symbol ? (
            <span
              className="inline-block h-6 w-6"
              style={{ color: n.symbolForeColor || 'currentColor' }}
              dangerouslySetInnerHTML={{ __html: n.symbol }}
            />
          ) : (
            <CarIcon className="h-6 w-6" />
          ),
          title: n.title,
          text: n.text,
        }))
      : defaultFeatures;

  return (
    <>
      {/* Hero — banner image if set; otherwise let the page background image (Design editor) show
          through a dark overlay; otherwise the default gradient. */}
      <section
        className={`relative min-h-[420px] w-full ${
          tenant?.bannerImageUrl || tenant?.backgroundImageUrl ? '' : 'bg-black'
        }`}
        style={
          tenant?.bannerImageUrl
            ? {
                backgroundImage: `url(${tenant.bannerImageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : {}
        }
      >
        {tenant?.bannerImageUrl ? (
          <div className="absolute inset-0 bg-black/60" />
        ) : tenant?.backgroundImageUrl ? (
          // Body carries the fixed background image; darken it here for legible hero text.
          <div className="absolute inset-0 bg-black/50" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900 to-black">
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  'linear-gradient(135deg, rgba(255,212,0,0.35) 0%, rgba(255,152,0,0.2) 50%, transparent 100%)',
              }}
            />
          </div>
        )}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Booking card */}
            <div className="booking-card bg-white rounded-lg shadow-2xl p-6 lg:p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('search.title')}</h2>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-base font-bold text-gray-800 mb-2">{t('search.startDate')}</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      value={startDate}
                      min={todayISO()}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-base font-bold text-gray-800 mb-2">{t('search.endDate')}</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      value={endDate}
                      min={startDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-base font-bold text-gray-800 mb-2">{t('search.category')}</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="">{t('search.selectCategory')}</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-base font-bold text-gray-800 mb-2">{t('search.city')}</label>
                <CitySearchInput
                  ref={cityRef}
                  cities={cities}
                  onSearch={(c) => changeCity(c)}
                />
              </div>

              <button
                onClick={() => {
                  cityRef.current?.submit();
                  scrollToModels();
                }}
                className="btn-primary w-full py-4 px-6 flex items-center justify-center"
              >
                {t('search.viewVehicles')} <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>

            {/* Promo + features */}
            <div className="text-white w-full">
              <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight">{heroTitle}</h1>
              <p className="text-2xl mb-8 text-gray-200">{heroDescription}</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                {features.map((f, i) => (
                  <div key={i} className="flex flex-col items-start">
                    <div
                      className="mb-2 h-12 w-12 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'var(--color-primary)' }}
                    >
                      {f.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-1">{f.title}</h3>
                    <p className="text-gray-300 text-sm">{f.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Available cars */}
      <section ref={modelsRef} className="py-20 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                {effectiveMode === 'models' ? t('results.availableModels') : t('results.availableCars')}
              </h2>
              {!loading && cars.length > 0 && (
                <span className="text-lg font-normal text-gray-600 bg-gray-100 px-4 py-1 rounded-full">
                  {cars.length}
                </span>
              )}
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('results.browseFleet', { name: tenant?.displayName || t('results.our') })}
            </p>
            {displayMode === 'both' && (
              <div className="mt-6">
                <ViewModeToggle mode={viewMode} onChange={setViewMode} />
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
              <p className="mt-4 text-gray-600">{t('results.loading')}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-600">{t('results.noneAvailable')}</div>
          ) : effectiveMode === 'models' ? (
            <ModelCategoryList cars={filtered} city={city} />
          ) : (
            <div className="space-y-16">
              {grouped.map(([categoryName, list]) => (
                <div key={categoryName} className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{categoryName}</h3>
                  </div>
                  <div className="overflow-x-auto pb-4 -mx-4 px-4 model-cards-scroll">
                    <div className="flex gap-6" style={{ minWidth: 'max-content' }}>
                      {list.map((car) => (
                        <VehicleCard key={car.id} car={car} />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Content sections after the first (section[0] feeds the hero above) — Design → Content tab.
          The "Sections" tab (sectionsJson) is NOT shown here — it renders on the About page. */}
      <SectionsRenderer json={tenant?.contentJson} startIndex={1} />

      {/* CTA */}
      <section className="bg-zinc-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('cta.title')}</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            {t('cta.text', { name: tenant?.displayName || t('cta.us') })}
          </p>
          <button
            onClick={scrollToModels}
            className="btn-primary py-3 px-8 inline-flex items-center"
          >
            {t('cta.browse')} <ArrowRight className="ml-2 h-5 w-5" />
          </button>
        </div>
      </section>
    </>
  );
}

function VehicleCard({ car }: { car: Car }) {
  const { t } = useTranslation();
  const tiers = tierList(car);
  // Badge shows the primary (first non-zero) tier; the footer lists every offered tier.
  const price = tiers.length > 0 ? tiers[0].amount : (car.priceFullTimePerWeek || car.pricePerWeek || 0);
  const unit = tiers.length > 0 ? t(`units.${tiers[0].unit}`) : t(`units.${priceUnit(car.pricingPeriod)}`);
  const title = car.title || `${car.brand ?? ''} ${car.model ?? ''}`.trim() || 'Car';
  return (
    <div className="vehicle-card flex-shrink-0" style={{ width: 320, minWidth: 320 }}>
      <div className="relative">
        <ModelImage make={car.brand} model={car.model} fallback={car.imageURL} alt={title} className="vehicle-image" />
        {price > 0 && (
          <div
            className="absolute top-4 right-4 text-black px-3 py-1 rounded-full text-sm font-semibold"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            ${price.toLocaleString()}/{unit}
          </div>
        )}
      </div>
      <div className="vehicle-info">
        <h3 className="vehicle-title">{title}</h3>
        <div className="vehicle-details space-y-2">
          {car.year ? (
            <div className="flex items-center text-gray-600">
              <Users className="h-4 w-4 mr-2" /> <span>{t('card.year', { year: car.year })}</span>
            </div>
          ) : null}
          {(car.city || car.state) && (
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-2" /> <span>{[car.city, car.state].filter(Boolean).join(', ')}</span>
            </div>
          )}
          {!!car.rating && (
            <div className="flex items-center text-gray-600">
              <Star className="h-4 w-4 mr-2 fill-current text-yellow-400" /> <span>{car.rating}</span>
            </div>
          )}
        </div>
        <div className="flex justify-between items-center mt-4">
          <div className="vehicle-price flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            {tiers.length > 0 ? (
              tiers.map((tr) => (
                <span key={tr.unit}>
                  ${fmtPrice(tr.amount)}<span className="text-sm text-gray-600">/{t(`units.${tr.unit}`)}</span>
                </span>
              ))
            ) : (
              <span>${price.toLocaleString()}<span className="text-sm text-gray-600">/{unit}</span></span>
            )}
          </div>
          <Link
            to={`/car/${car.id}`}
            className="btn-primary text-sm py-2 px-4"
          >
            {t('card.book')}
          </Link>
        </div>
      </div>
    </div>
  );
}
